import { CheckCircle, ArrowRight } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { useNavigate } from "react-router-dom";
import { useGuarantee } from "@/hooks/useGuarantee";
import {
  GUARANTEE_PRESUBSCRIBE_COPY,
  guaranteeCopyFor,
} from "@/utils/guaranteeCopy";

export function PostTrialBanner() {
  const { trial, plan } = useBilling();
  const { guarantee, loading: guaranteeLoading } = useGuarantee();
  const navigate = useNavigate();

  if (!trial?.used || plan?.subscription?.status === "active" || plan?.subscription?.status === "trialing") {
    return null;
  }

  // Fail closed: while the lookup is in flight we do not know whether this
  // merchant already has a frozen target (a lapsed subscriber does), and the
  // pre-subscribe wording would be wrong for them. Render no promise until we
  // know which one is true.
  const guaranteeCopy = guaranteeLoading
    ? null
    : guarantee
      ? guaranteeCopyFor(guarantee)
      : GUARANTEE_PRESUBSCRIBE_COPY;

  return (
    <div className="glass-panel border-emerald-100 bg-gradient-to-r from-emerald-50/60 to-teal-50/60">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-200">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">
              Your free AR product is live. Ready to roll AR out to more products?
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Choose a plan to keep generating and unlock higher-quality output for your most important products.
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-4">
            <h4 className="text-sm font-semibold text-slate-900">
              Launch — The AR Starter Programme
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              Up to 15 products in AR each month. Quick Capture + Fast Render. Premium Generation $15/product.
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">$49/mo</p>
          </div>

          <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-sky-50/80 p-4 shadow-lg shadow-indigo-100">
            <div className="mb-1 inline-block rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Best value for growing stores
            </div>
            <h4 className="text-sm font-semibold text-slate-900">
              Growth — The AR Sales Engine
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              Up to 60 products/month. 5 Premium Generations included. Priority render queue.
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">$149/mo</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white/80 p-4">
            <h4 className="text-sm font-semibold text-slate-900">
              Scale — The Full-Catalog Programme
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              Up to 250 products/month. Unlimited Premium Generations. Highest queue priority.
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">$399/mo</p>
          </div>
        </div>

        {guaranteeCopy && (
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-xs text-indigo-800">
            <p className="font-medium">
              The AR Live Guarantee: {guaranteeCopy.headline}
            </p>
            <p className="mt-1 text-indigo-700">{guaranteeCopy.detail}</p>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-slate-700">
            Choose a plan to keep generating new AR models.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/billing")}
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl"
            >
              Choose a plan
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
